import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { BulkJob, NotificationChannel } from '../types/notificationMessage';

const call = apiClient.request as unknown as (...a: unknown[]) => Promise<[unknown, unknown]>;

export interface BulkRecipientInput {
  userId?: string;
  external?: string;
  variables?: Record<string, string>;
}

/**
 * 회원 수신자 조건 검색 (Segment Query) — BE AdminSegmentQuery 정합(소스 검증).
 *
 * <p>필드 = nationality / status / limit / gradeId(D4) 만. 채워진 필드만 AND 결합.
 * status 미지정 시 WITHDRAWN 자동 제외(BE 정책). limit 미지정 시 BE 1000 default·10000 cap.
 * gradeId = user-service 등급 마스터 id(useUserGradesQuery 의 gradeId 와 1:1).
 */
export interface BulkSegmentInput {
  nationality?: string;
  status?: 'ACTIVE' | 'DORMANT' | 'WITHDRAWN';
  /** 회원 등급 필터(등급별 발송, D4). user-service 등급 id. */
  gradeId?: string;
  limit?: number;
}

export interface BulkDispatchInput {
  channel: NotificationChannel;
  templateCode?: string;
  rawSubject?: string;
  rawBody?: string;
  recipients?: BulkRecipientInput[];
  segmentQuery?: BulkSegmentInput;
  defaultVariables?: Record<string, string>;
  /** 예약 발송 ISO datetime. 미지정 시 즉시 발송. */
  scheduledAt?: string;
  /**
   * QA-419 정보성 메시지 — true 면 BE 가 회원 수신자의 마케팅 수신 동의를 보지 않고 보낸다(계좌·CS·상품 안내).
   * BE 반영 전에는 무시된다(모르는 필드) — 그동안 미동의 회원은 건너뛴다.
   */
  informational?: boolean;
  /** QA-419 — EMAIL rawBody 가 HTML(에디터 본문·이미지)이면 true. BE 가 text/html 로 보낸다(전달 문서). */
  rawHtml?: boolean;
}

export const useBulkDispatchMutation = (): UseMutationResult<BulkJob, Error, BulkDispatchInput> =>
  useMutation<BulkJob, Error, BulkDispatchInput>({
    mutationFn: async (input) => {
      const [data] = await call('post', '/admin/v1/notification/dispatch/bulk', { body: input });
      return data as BulkJob;
    },
  });
